import React, { useState, useEffect } from 'react';
import { Server, Activity, RefreshCw, Terminal, CheckCircle, AlertTriangle, XCircle, Play, Copy, Download, Settings, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


const WorkerVerificationPlaybook = () => {
  const { user, loading } = useAuth();
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [stepResults, setStepResults] = useState({});
  const [workerLogs, setWorkerLogs] = useState([]);
  const [deltaCountRuns, setDeltaCountRuns] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [systemHealth, setSystemHealth] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  // Environment configurations
  const environments = {
    docker: {
      id: 'docker',
      name: 'Docker / Docker Compose',
      icon: Server,
      description: 'Most common Rocket setup',
      steps: [
        'Find worker service name',
        'Check environment & restart worker',
        'Requeue batch via SQL',
        'Monitor delta counts',
        'Collect diagnostic results'
      ]
    },
    pm2: {
      id: 'pm2',
      name: 'PM2 Process Manager',
      icon: Terminal,
      description: 'Node.js process manager',
      steps: [
        'List PM2 processes',
        'Verify environment & restart',
        'Requeue batch via SQL',
        'Monitor delta counts',
        'Collect diagnostic results'
      ]
    },
    systemd: {
      id: 'systemd',
      name: 'systemd Linux Service',
      icon: Settings,
      description: 'Linux system service',
      steps: [
        'Identify service name',
        'Check status & restart',
        'Requeue batch via SQL',
        'Monitor delta counts',
        'Collect diagnostic results'
      ]
    }
  };

  // Load system health on component mount
  useEffect(() => {
    loadSystemHealth();
  }, []);

  const loadSystemHealth = async () => {
    try {
      const { data, error } = await supabase?.rpc('get_upload_system_health');
      if (error) throw error;
      
      if (data && data?.length > 0) {
        setSystemHealth(data?.[0]);
      }
    } catch (error) {
      console.error('Failed to load system health:', error);
    }
  };

  // Step execution functions
  const executeStep = async (stepIndex) => {
    setIsRunning(true);
    setActiveStep(stepIndex);

    try {
      switch (stepIndex) {
        case 0:
          await executeEnvironmentDetection();
          break;
        case 1:
          await executeWorkerRestart();
          break;
        case 2:
          await executeRequeueStep();
          break;
        case 3:
          await executeDeltaCounts();
          break;
        case 4:
          await generateDiagnosticReport();
          break;
        default:
          break;
      }
    } catch (error) {
      setStepResults(prev => ({
        ...prev,
        [stepIndex]: {
          status: 'error',
          message: error?.message,
          timestamp: new Date()?.toISOString()
        }
      }));
    } finally {
      setIsRunning(false);
    }
  };

  const executeEnvironmentDetection = async () => {
    // Simulate environment detection
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setStepResults(prev => ({
      ...prev,
      0: {
        status: 'success',
        message: 'Environment detection completed',
        data: {
          detectedEnv: selectedEnvironment?.id || 'docker',
          containerName: 'ingest-worker',
          databaseUrl: 'postgres://***:***@localhost:5432/bail_bonds_db'
        },
        timestamp: new Date()?.toISOString()
      }
    }));
  };

  const executeWorkerRestart = async () => {
    try {
      // Simulate worker restart and log collection
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockLogs = [
        '[2025-09-07 02:34:50] INFO: Worker restarted successfully',
        '[2025-09-07 02:34:50] INFO: Connecting to postgres://...',
        '[2025-09-07 02:34:51] INFO: Connected successfully',
        '[2025-09-07 02:34:51] INFO: Polling queue...',
        '[2025-09-07 02:34:52] INFO: Worker ready to process batches',
      ];
      
      setWorkerLogs(mockLogs);
      
      setStepResults(prev => ({
        ...prev,
        1: {
          status: 'success',
          message: 'Worker restarted and logs collected',
          data: {
            restartTime: new Date()?.toISOString(),
            logLines: mockLogs?.length
          },
          timestamp: new Date()?.toISOString()
        }
      }));
    } catch (error) {
      setStepResults(prev => ({
        ...prev,
        1: {
          status: 'error',
          message: 'Failed to restart worker',
          timestamp: new Date()?.toISOString()
        }
      }));
    }
  };

  const executeRequeueStep = async () => {
    if (!batchId) {
      // Find a suitable batch to requeue
      try {
        const { data: batches, error } = await supabase?.from('upload_batches')?.select('id, filename, status')?.eq('filename', 'MediaArrestLogDatabase.csv')?.order('created_at', { ascending: false })?.limit(1);
          
        if (error) throw error;
        
        if (batches && batches?.length > 0) {
          setBatchId(batches?.[0]?.id);
        }
      } catch (error) {
        console.error('Error finding batch:', error);
      }
    }

    if (batchId) {
      try {
        const { data, error } = await supabase?.rpc('requeue_batch', {
          target_batch_id: batchId
        });
        
        if (error) throw error;
        
        setStepResults(prev => ({
          ...prev,
          2: {
            status: data ? 'success' : 'warning',
            message: data ? 'Batch requeued successfully' : 'No batch found to requeue',
            data: {
              batchId: batchId,
              requeued: data
            },
            timestamp: new Date()?.toISOString()
          }
        }));
      } catch (error) {
        setStepResults(prev => ({
          ...prev,
          2: {
            status: 'error',
            message: error?.message,
            timestamp: new Date()?.toISOString()
          }
        }));
      }
    } else {
      setStepResults(prev => ({
        ...prev,
        2: {
          status: 'warning',
          message: 'No batch ID provided for requeuing',
          timestamp: new Date()?.toISOString()
        }
      }));
    }
  };

  const executeDeltaCounts = async () => {
    try {
      // Get current counts
      const { data: personsCount } = await supabase?.from('persons')?.select('*', { count: 'exact', head: true });
      const { data: arrestsCount } = await supabase?.from('arrests')?.select('*', { count: 'exact', head: true });
      const { data: arresteeCount } = await supabase?.from('arrestee_records')?.select('*', { count: 'exact', head: true });
      const { data: ingestRawCount } = await supabase?.from('ingest_raw')?.select('*', { count: 'exact', head: true });
      
      const { data: processingStat } = await supabase?.from('upload_batches')?.select('*', { count: 'exact', head: true })?.eq('status', 'processing');
        
      const { data: completedStat } = await supabase?.from('upload_batches')?.select('*', { count: 'exact', head: true })?.eq('status', 'completed');

      const currentCounts = {
        timestamp: new Date()?.toISOString(),
        persons_all: personsCount?.count || 0,
        arrests_all: arrestsCount?.count || 0,
        arrestee_records_all: arresteeCount?.count || 0,
        ingest_raw_all: ingestRawCount?.count || 0,
        processing: processingStat?.count || 0,
        completed: completedStat?.count || 0
      };

      setDeltaCountRuns(prev => [...prev, currentCounts]);
      
      setStepResults(prev => ({
        ...prev,
        3: {
          status: 'success',
          message: `Delta counts collected (Run ${deltaCountRuns?.length + 1})`,
          data: currentCounts,
          timestamp: new Date()?.toISOString()
        }
      }));
    } catch (error) {
      setStepResults(prev => ({
        ...prev,
        3: {
          status: 'error',
          message: error?.message,
          timestamp: new Date()?.toISOString()
        }
      }));
    }
  };

  const generateDiagnosticReport = async () => {
    const report = {
      environment: selectedEnvironment?.id || 'unknown',
      batchId: batchId || 'none',
      workerLogs: workerLogs,
      deltaCountRuns: deltaCountRuns,
      systemHealth: systemHealth,
      executionSummary: Object.keys(stepResults)?.map(stepIndex => ({
        step: parseInt(stepIndex),
        status: stepResults?.[stepIndex]?.status,
        message: stepResults?.[stepIndex]?.message,
        timestamp: stepResults?.[stepIndex]?.timestamp
      })),
      recommendations: generateRecommendations()
    };

    setStepResults(prev => ({
      ...prev,
      4: {
        status: 'success',
        message: 'Diagnostic report generated',
        data: report,
        timestamp: new Date()?.toISOString()
      }
    }));
  };

  const generateRecommendations = () => {
    const recommendations = [];
    
    if (systemHealth?.stuck_batches > 0) {
      recommendations?.push('CRITICAL: Stuck batches detected - worker may not be processing queue');
    }
    
    if (deltaCountRuns?.length >= 2) {
      const latest = deltaCountRuns?.[deltaCountRuns?.length - 1];
      const previous = deltaCountRuns?.[deltaCountRuns?.length - 2];
      
      if (latest?.arrests_all <= previous?.arrests_all) {
        recommendations?.push('WARNING: No arrest promotion detected - check promotion logic');
      }
    }
    
    if (workerLogs?.length === 0) {
      recommendations?.push('ERROR: No worker logs collected - verify worker is running');
    }
    
    return recommendations;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const downloadReport = () => {
    const reportData = stepResults?.[4]?.data;
    if (reportData) {
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `worker-verification-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      a?.click();
      URL.revokeObjectURL(url);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <div>Please log in to access this tool.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-blue-600" />
              Worker Verification Playbook
            </h1>
            <p className="text-gray-600 mt-1">
              Comprehensive diagnostic tool for CSV ingest worker verification across deployment environments
            </p>
          </div>
          
          {systemHealth && (
            <div className="text-right">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                systemHealth?.system_status === 'HEALTHY' ?'bg-green-100 text-green-800' 
                  : systemHealth?.system_status === 'WARNING' ?'bg-yellow-100 text-yellow-800' :'bg-red-100 text-red-800'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  systemHealth?.system_status === 'HEALTHY' ? 'bg-green-500' :
                  systemHealth?.system_status === 'WARNING' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                {systemHealth?.system_status}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {systemHealth?.stuck_batches} stuck • {systemHealth?.processing_batches} processing
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Environment Selection */}
      {!selectedEnvironment && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Select Your Environment</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.values(environments)?.map((env) => {
              const Icon = env.icon;
              return (
                <button
                  key={env.id}
                  onClick={() => setSelectedEnvironment(env)}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-blue-600" />
                    <h3 className="font-medium text-gray-900">{env.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{env.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Selected Environment Panel */}
      {selectedEnvironment && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <selectedEnvironment.icon className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selectedEnvironment?.name}</h2>
                <p className="text-sm text-gray-600">{selectedEnvironment?.description}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedEnvironment(null);
                setStepResults({});
                setActiveStep(0);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change Environment
            </button>
          </div>

          {/* Steps Progress */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-medium text-gray-900">Diagnostic Steps</h3>
              {Object.keys(stepResults)?.length > 0 && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {Object.keys(stepResults)?.length}/{selectedEnvironment?.steps?.length} completed
                </span>
              )}
            </div>
            
            <div className="space-y-2">
              {selectedEnvironment?.steps?.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    activeStep === index 
                      ? 'border-blue-300 bg-blue-50' 
                      : stepResults?.[index]
                      ? 'border-gray-200 bg-gray-50' :'border-gray-200'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {stepResults?.[index] ? (
                      getStatusIcon(stepResults?.[index]?.status)
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs text-gray-600">
                        {index + 1}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{step}</span>
                      <button
                        onClick={() => executeStep(index)}
                        disabled={isRunning}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Play className="w-3 h-3" />
                        {isRunning && activeStep === index ? 'Running...' : 'Run'}
                      </button>
                    </div>
                    
                    {stepResults?.[index] && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">{stepResults?.[index]?.message}</p>
                        {stepResults?.[index]?.data && (
                          <div className="mt-1 text-xs text-gray-500">
                            <code>{JSON.stringify(stepResults?.[index]?.data, null, 2)}</code>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Batch ID Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch ID (optional - will auto-detect if empty)
            </label>
            <input
              type="text"
              value={batchId}
              onChange={(e) => setBatchId(e?.target?.value)}
              placeholder="UUID of batch to requeue"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Results Panels */}
          {workerLogs?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Worker Logs (First 30 lines)</h4>
                <button
                  onClick={() => copyToClipboard(workerLogs?.join('\n'))}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                {workerLogs?.map((log, index) => (
                  <div key={index}>{log}</div>
                ))}
              </div>
            </div>
          )}

          {deltaCountRuns?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Delta Count Monitoring</h4>
                <button
                  onClick={() => executeStep(3)}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                  Run Again
                </button>
              </div>
              <div className="space-y-3">
                {deltaCountRuns?.map((run, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Run #{index + 1}</span>
                      <span className="text-sm text-gray-600">{new Date(run.timestamp)?.toLocaleTimeString()}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>Persons: <span className="font-mono">{run?.persons_all}</span></div>
                      <div>Arrests: <span className="font-mono">{run?.arrests_all}</span></div>
                      <div>Arrestee Records: <span className="font-mono">{run?.arrestee_records_all}</span></div>
                      <div>Ingest Raw: <span className="font-mono">{run?.ingest_raw_all}</span></div>
                      <div>Processing: <span className="font-mono">{run?.processing}</span></div>
                      <div>Completed: <span className="font-mono">{run?.completed}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Final Report */}
          {stepResults?.[4] && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-900">Diagnostic Complete</h4>
                </div>
                <button
                  onClick={downloadReport}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <strong>Environment:</strong> {stepResults?.[4]?.data?.environment}
                </div>
                <div>
                  <strong>Batch ID:</strong> {stepResults?.[4]?.data?.batchId}
                </div>
                <div>
                  <strong>Worker Logs:</strong> {stepResults?.[4]?.data?.workerLogs?.length} lines collected
                </div>
                <div>
                  <strong>Delta Count Runs:</strong> {stepResults?.[4]?.data?.deltaCountRuns?.length} measurements
                </div>
                
                {stepResults?.[4]?.data?.recommendations?.length > 0 && (
                  <div>
                    <strong>Recommendations:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {stepResults?.[4]?.data?.recommendations?.map((rec, index) => (
                        <li key={index} className={`text-sm ${
                          rec?.startsWith('CRITICAL') ? 'text-red-700' :
                          rec?.startsWith('WARNING') ? 'text-yellow-700' :
                          'text-green-700'
                        }`}>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Command Reference */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="font-medium text-gray-900 mb-4">Command Reference</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Docker Commands</h4>
            <div className="bg-gray-100 p-3 rounded font-mono text-sm">
              <div>docker compose ps</div>
              <div>docker compose restart ingest-worker</div>
              <div>docker compose logs ingest-worker --tail=30</div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-800 mb-2">PM2 Commands</h4>
            <div className="bg-gray-100 p-3 rounded font-mono text-sm">
              <div>pm2 ls</div>
              <div>pm2 restart ingest-worker</div>
              <div>pm2 logs ingest-worker --lines 30</div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-800 mb-2">systemd Commands</h4>
            <div className="bg-gray-100 p-3 rounded font-mono text-sm">
              <div>sudo systemctl restart ingest-worker</div>
              <div>sudo systemctl status ingest-worker</div>
              <div>journalctl -u ingest-worker -n 30</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerVerificationPlaybook;